import { API_URLS, getAccessToken } from "./urls";
import { DeveloperTokenManager } from '../utils/developerTokenManager';

const activeShareCalls = new Set<string>();

interface ShareFeedbackResponse {
  success: boolean;
  message: string;
  developer_feedback_urls: Array<{ email: string; url: string }>;
}

export interface VerifyDeveloperTokenResponse {
  access_token: string;
  token_type: string;
}

export const verifyDeveloperToken = async (
  code: string
): Promise<{ success: boolean; data?: VerifyDeveloperTokenResponse; error?: string }> => {
  try {
    const response = await fetch(API_URLS.VERIFY_DEVELOPER_TOKEN, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, error: `HTTP ${response.status}: ${errorText}` };
    }
    const data: VerifyDeveloperTokenResponse = await response.json();
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Token verification failed" };
  }
};

export const shareFeedbackWithDeveloper = async (
  cardId: number,
  emails: string[],
  workspaceId?: number
): Promise<{ success: boolean; data?: ShareFeedbackResponse; error?: string }> => {
  const callId = `${cardId}-${emails.sort().join(",")}-${workspaceId || "no-workspace"}`;
  if (activeShareCalls.has(callId)) {
    throw new Error("Share already in progress for this card and email combination");
  }
  activeShareCalls.add(callId);
  try {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error("No access token available");
    const { valid: validEmails, invalid: invalidEmails } = validateEmails(emails);
    if (invalidEmails.length > 0) throw new Error(`Invalid email addresses: ${invalidEmails.join(", ")}`);
    if (validEmails.length === 0) throw new Error("No valid email addresses provided");
    const uniqueValidEmails = Array.from(new Set(validEmails.map((e) => e.trim().toLowerCase())));
    const requestBody = { developer_emails: uniqueValidEmails, ...(workspaceId && { workspace_id: workspaceId }) };
    const response = await fetch(API_URLS.SHARE_FEEDBACK_WITH_DEVELOPER_BY_CARD_ID(cardId), {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.detail && Array.isArray(errorJson.detail)) {
              errorMessage = `Validation Error: ${errorJson.detail.map((e: any) => `${e.loc?.join(".")} - ${e.msg}`).join(", ")}`;
            } else {
              errorMessage = errorJson.message || errorJson.detail || errorMessage;
            }
          } catch { errorMessage = errorText; }
        }
      } catch {}
      throw new Error(errorMessage);
    }
    const result: ShareFeedbackResponse = await response.json();
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Share failed" };
  } finally {
    activeShareCalls.delete(callId);
  }
};

export const verifyCardShare = (originalCardId: number, shareResponse: ShareFeedbackResponse): { isValid: boolean; issues: string[] } => {
  const issues: string[] = [];
  if (!shareResponse.developer_feedback_urls || shareResponse.developer_feedback_urls.length === 0) {
    issues.push("No developer URLs returned");
    return { isValid: false, issues };
  }
  shareResponse.developer_feedback_urls.forEach((urlItem, index) => {
    const urlIndex = index + 1;
    const match = urlItem.url.match(/view-card\/(\d+)/);
    const extractedCardId = match ? match[1] : null;
    if (!extractedCardId) issues.push(`URL ${urlIndex} does not contain a valid card ID`);
    else if (extractedCardId !== originalCardId.toString()) issues.push(`URL ${urlIndex} has wrong card ID`);
    if (!urlItem.url.includes("code=")) issues.push(`URL ${urlIndex} is missing authentication code`);
    if (!urlItem.url.includes("developer=")) issues.push(`URL ${urlIndex} is missing developer parameter`);
    if (!urlItem.email || !isValidEmail(urlItem.email)) issues.push(`URL ${urlIndex} has invalid developer email`);
  });
  return { isValid: issues.length === 0, issues };
};

export const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const validateEmails = (emails: string[]): { valid: string[]; invalid: string[] } => {
  const valid: string[] = [];
  const invalid: string[] = [];
  emails.forEach((email) => {
    const trimmed = email.trim();
    if (trimmed && isValidEmail(trimmed)) valid.push(trimmed);
    else if (trimmed) invalid.push(trimmed);
  });
  return { valid, invalid };
};

export const deleteCard = async (cardId: number): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return { success: false, error: "No access token available" };
    const response = await fetch(API_URLS.DELETE_CARD(cardId), {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, error: `HTTP error! status: ${response.status}, message: ${errorText}` };
    }
    let result;
    try { result = await response.json(); } catch { result = { message: "Card deleted successfully" }; }
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};
